import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../utils/api";
import { useTheme } from "../contexts/ThemeContext";
import { Save, Check, X, Mail, CreditCard, HardDrive, Palette, Server } from "lucide-react";

const TABS = [
  { key: "theme", label: "Theme & Branding", icon: Palette },
  { key: "smtp", label: "Email (SMTP)", icon: Mail },
  { key: "stripe", label: "Stripe Billing", icon: CreditCard },
  { key: "storage", label: "File Storage", icon: HardDrive },
  { key: "support", label: "Support & Contact", icon: Server },
];

export default function SettingsPage() {
  const { reload } = useTheme();
  const [tab, setTab] = useState("theme");
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  const load = async () => {
    const r = await api.get("/settings/");
    setS(r.data);
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put("/settings/", patch);
      await load();
      await reload();
      setFlash({ type: "success", text: "Saved." });
    } catch (e) {
      setFlash({ type: "error", text: e.response?.data?.detail || "Save failed" });
    } finally {
      setSaving(false);
      setTimeout(() => setFlash(null), 3000);
    }
  };

  const testSmtp = async () => {
    try {
      const r = await api.post("/settings/test-smtp");
      setFlash({ type: r.data.ok ? "success" : "error", text: r.data.ok ? "Test email sent." : "Test failed: " + r.data.error });
    } catch (e) {
      setFlash({ type: "error", text: e.response?.data?.detail || "Test failed" });
    }
    setTimeout(() => setFlash(null), 5000);
  };

  if (!s) return <Layout><div style={{ padding: "3rem", textAlign: "center", color: "var(--text-muted)" }}>Loading…</div></Layout>;

  return (
    <Layout>
      <div className="animate-in" style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ marginBottom: "1.5rem" }}>
          <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.5rem", margin: 0 }}>App Settings</h1>
          <p style={{ color: "var(--text-muted)", fontSize: "0.85rem", margin: "0.25rem 0 0" }}>Superadmin-only. Theme applies instantly across all users after save.</p>
        </div>

        {flash && (
          <div data-testid="settings-flash" style={{ marginBottom: "1rem", padding: "0.55rem 0.9rem", borderRadius: 8, background: flash.type === "success" ? "#DCFCE7" : "#FEE2E2", color: flash.type === "success" ? "#166534" : "#991B1B", border: `1px solid ${flash.type === "success" ? "#86EFAC" : "#FCA5A5"}`, fontSize: "0.82rem" }}>{flash.text}</div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "220px 1fr", gap: "1.5rem" }}>
          <div>
            {TABS.map(t => {
              const I = t.icon; const on = tab === t.key;
              return (
                <button key={t.key} data-testid={`settings-tab-${t.key}`} onClick={() => setTab(t.key)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: "0.55rem", padding: "0.6rem 0.8rem", background: on ? "var(--sage-light)" : "transparent", color: on ? "var(--sage)" : "var(--text-secondary)", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: on ? 600 : 500, fontSize: "0.88rem", marginBottom: "0.25rem", textAlign: "left" }}>
                  <I size={15} /> {t.label}
                </button>
              );
            })}
          </div>

          <div className="recon-card" style={{ padding: "1.5rem" }}>
            {tab === "theme" && <ThemeTab s={s} save={save} saving={saving} />}
            {tab === "smtp" && <SmtpTab s={s} save={save} saving={saving} onTest={testSmtp} />}
            {tab === "stripe" && <StripeTab s={s} save={save} saving={saving} />}
            {tab === "storage" && <StorageTab s={s} save={save} saving={saving} />}
            {tab === "support" && <SupportTab s={s} save={save} saving={saving} />}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: "1rem" }}>
      <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "0.3rem" }}>{label}</label>
      {children}
      {hint && <p style={{ margin: "0.3rem 0 0", fontSize: "0.72rem", color: "var(--text-muted)" }}>{hint}</p>}
    </div>
  );
}

function SaveBtn({ onClick, saving, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={saving} className="btn-primary"
      style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", padding: "0.5rem 1.1rem", fontSize: "0.85rem" }}>
      <Save size={14} /> {saving ? "Saving…" : "Save changes"}
    </button>
  );
}

function ThemeTab({ s, save, saving }) {
  const [color, setColor] = useState(s.theme?.primary_color || "#5C7A5E");
  const [appName, setAppName] = useState(s.app_name || "ReconX");
  const [tagline, setTagline] = useState(s.app_tagline || "");
  const PRESETS = [
    ["Sage", "#5C7A5E"], ["Navy", "#1E3A5F"], ["Plum", "#6B2D5C"],
    ["Terracotta", "#B85C38"], ["Teal", "#2C7873"], ["Graphite", "#3A3A3A"],
  ];
  return (
    <>
      <h2 style={{ margin: "0 0 1rem", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.15rem" }}>Theme & Branding</h2>
      <Field label="Primary color" hint="Applies to buttons, links, highlights, and landing-page accents. Other colors derive from this.">
        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
          <input data-testid="theme-color-picker" type="color" value={color} onChange={e => setColor(e.target.value)} style={{ width: 52, height: 36, border: "1px solid var(--border-default)", borderRadius: 6, background: "white", cursor: "pointer" }} />
          <input data-testid="theme-color-hex" type="text" className="recon-input" value={color} onChange={e => setColor(e.target.value)} style={{ width: 120, fontFamily: "JetBrains Mono, monospace" }} />
          <div style={{ display: "flex", gap: "0.3rem", marginLeft: "0.5rem", flexWrap: "wrap" }}>
            {PRESETS.map(([name, hex]) => (
              <button key={hex} data-testid={`theme-preset-${name.toLowerCase()}`} onClick={() => setColor(hex)}
                style={{ padding: "0.35rem 0.6rem", background: hex, color: "white", border: color === hex ? "2px solid #111" : "1px solid rgba(0,0,0,0.1)", borderRadius: 6, cursor: "pointer", fontSize: "0.72rem", fontWeight: 600 }}>
                {name}
              </button>
            ))}
          </div>
        </div>
      </Field>
      <Field label="App name">
        <input data-testid="theme-app-name" className="recon-input" value={appName} onChange={e => setAppName(e.target.value)} />
      </Field>
      <Field label="Tagline" hint="Appears on the landing-page hero + browser tab title.">
        <input data-testid="theme-tagline" className="recon-input" value={tagline} onChange={e => setTagline(e.target.value)} />
      </Field>
      <SaveBtn testid="theme-save" saving={saving} onClick={() => save({ theme: { primary_color: color }, app_name: appName, app_tagline: tagline })} />
    </>
  );
}

function SmtpTab({ s, save, saving, onTest }) {
  const [cfg, setCfg] = useState(s.smtp || {});
  const f = (k, v) => setCfg(p => ({ ...p, [k]: v }));
  return (
    <>
      <h2 style={{ margin: "0 0 1rem", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.15rem" }}>Email (SMTP)</h2>
      <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", margin: "0 0 1rem" }}>Used for purchase request notifications, recon completion alerts, and any future outbound email.</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Host"><input data-testid="smtp-host" className="recon-input" value={cfg.host || ""} onChange={e => f("host", e.target.value)} placeholder="smtp.sendgrid.net" /></Field>
        <Field label="Port"><input data-testid="smtp-port" type="number" className="recon-input" value={cfg.port ?? 587} onChange={e => f("port", Number(e.target.value))} /></Field>
        <Field label="Username"><input data-testid="smtp-username" className="recon-input" value={cfg.username || ""} onChange={e => f("username", e.target.value)} /></Field>
        <Field label="Password (stored encrypted)"><input data-testid="smtp-password" type="password" className="recon-input" value={cfg.password || ""} onChange={e => f("password", e.target.value)} placeholder="Leave as '••••••••' to keep existing" /></Field>
        <Field label='"From" address'><input data-testid="smtp-from" className="recon-input" value={cfg.from_addr || ""} onChange={e => f("from_addr", e.target.value)} placeholder="no-reply@yourdomain.com" /></Field>
        <Field label="Use TLS">
          <label style={{ display: "flex", alignItems: "center", gap: "0.45rem", fontSize: "0.85rem", paddingTop: "0.4rem" }}>
            <input data-testid="smtp-tls" type="checkbox" checked={cfg.use_tls !== false} onChange={e => f("use_tls", e.target.checked)} /> STARTTLS on 587 (default on)
          </label>
        </Field>
      </div>
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
        <SaveBtn testid="smtp-save" saving={saving} onClick={() => save({ smtp: cfg })} />
        <button data-testid="smtp-test" onClick={onTest} className="btn-secondary" style={{ padding: "0.5rem 1.1rem", fontSize: "0.85rem" }}>Send test email</button>
      </div>
    </>
  );
}

function StripeTab({ s, save, saving }) {
  const [cfg, setCfg] = useState(s.stripe || {});
  const f = (k, v) => setCfg(p => ({ ...p, [k]: v }));
  return (
    <>
      <h2 style={{ margin: "0 0 0.25rem", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.15rem" }}>Stripe Billing</h2>
      <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", margin: "0 0 1rem" }}>When enabled, the landing page "Buy" button opens Stripe Checkout. Otherwise, purchase interest is captured and emailed to the support address.</p>
      <Field label="Enable Stripe Checkout">
        <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", fontSize: "0.88rem" }}>
          <input data-testid="stripe-enabled" type="checkbox" checked={!!cfg.enabled} onChange={e => f("enabled", e.target.checked)} />
          {cfg.enabled ? "Live payments on" : "Capture-mode (email admin on purchase intent)"}
        </label>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Publishable key"><input data-testid="stripe-pub" className="recon-input" value={cfg.publishable_key || ""} onChange={e => f("publishable_key", e.target.value)} placeholder="pk_live_…" /></Field>
        <Field label="Secret key"><input data-testid="stripe-secret" className="recon-input" value={cfg.secret_key || ""} onChange={e => f("secret_key", e.target.value)} placeholder="sk_live_…" /></Field>
        <Field label="Webhook signing secret"><input data-testid="stripe-wh" className="recon-input" value={cfg.webhook_secret || ""} onChange={e => f("webhook_secret", e.target.value)} placeholder="whsec_…" /></Field>
        <Field label="Default currency">
          <select data-testid="stripe-currency" className="recon-input" value={cfg.currency || "eur"} onChange={e => f("currency", e.target.value)}>
            <option value="eur">EUR (€)</option><option value="usd">USD ($)</option><option value="gbp">GBP (£)</option>
          </select>
        </Field>
      </div>
      <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: "0.25rem 0 0.9rem" }}>Webhook endpoint: <code>{(typeof window !== "undefined" ? window.location.origin : "")}/api/webhook/stripe</code></p>
      <SaveBtn testid="stripe-save" saving={saving} onClick={() => save({ stripe: cfg })} />
    </>
  );
}

function StorageTab({ s, save, saving }) {
  const cfg = s.storage || {};
  const [backend, setBackend] = useState(cfg.backend || "local");
  const [r2, setR2] = useState(cfg.r2 || {});
  const f = (k, v) => setR2(p => ({ ...p, [k]: v }));
  return (
    <>
      <h2 style={{ margin: "0 0 0.25rem", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.15rem" }}>File Storage</h2>
      <p style={{ fontSize: "0.82rem", color: "var(--text-muted)", margin: "0 0 1rem" }}>Local storage is always the working copy. Toggle Cloudflare R2 on to also mirror uploads off-box so your Linux disk doesn't bloat.</p>
      <Field label="Backend">
        <div style={{ display: "flex", gap: "0.5rem" }}>
          {["local", "r2"].map(b => (
            <button key={b} data-testid={`storage-backend-${b}`} onClick={() => setBackend(b)}
              style={{ padding: "0.5rem 1rem", border: `1px solid ${backend === b ? "var(--sage)" : "var(--border-default)"}`, background: backend === b ? "var(--sage-light)" : "white", color: backend === b ? "var(--sage)" : "var(--text-secondary)", borderRadius: 8, cursor: "pointer", fontSize: "0.85rem", fontWeight: backend === b ? 600 : 500 }}>
              {b === "local" ? "Local only" : "Cloudflare R2 (S3-compatible)"}
            </button>
          ))}
        </div>
      </Field>
      {backend === "r2" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
          <Field label="Endpoint URL"><input data-testid="r2-endpoint" className="recon-input" value={r2.endpoint_url || ""} onChange={e => f("endpoint_url", e.target.value)} placeholder="https://<account>.r2.cloudflarestorage.com" /></Field>
          <Field label="Bucket"><input data-testid="r2-bucket" className="recon-input" value={r2.bucket || ""} onChange={e => f("bucket", e.target.value)} /></Field>
          <Field label="Access key"><input data-testid="r2-access" className="recon-input" value={r2.access_key || ""} onChange={e => f("access_key", e.target.value)} /></Field>
          <Field label="Secret key"><input data-testid="r2-secret" className="recon-input" value={r2.secret_key || ""} onChange={e => f("secret_key", e.target.value)} /></Field>
        </div>
      )}
      <SaveBtn testid="storage-save" saving={saving} onClick={() => save({ storage: { backend, r2 } })} />
    </>
  );
}

function SupportTab({ s, save, saving }) {
  const [email, setEmail] = useState(s.support_email || "hello@krri.sh");
  return (
    <>
      <h2 style={{ margin: "0 0 1rem", fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 700, fontSize: "1.15rem" }}>Support & Contact</h2>
      <Field label="Support email" hint="Used on the landing-page CTA and as the recipient of purchase-request emails.">
        <input data-testid="support-email" className="recon-input" value={email} onChange={e => setEmail(e.target.value)} />
      </Field>
      <SaveBtn testid="support-save" saving={saving} onClick={() => save({ support_email: email })} />
    </>
  );
}
